/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   detail event thunk
 */

import {
  selectAddress,
  selectIsAddressInactive,
  selectIsOnlyActiveUnitOfMultiUnit,
  selectIsSingleUnit,
} from '@zcode-ui/perm-address'
import {
  getUser,
  isDetailPageNew,
  selectIsAddressOnBuilding,
} from '@zcode-ui/web-zumper-global-state'
import {
  event,
  getDetailEventProperties,
  type getEntityProperties,
  type getTraktorEntityId,
} from '@zcode-ui/web-zumper-integrations'
import type { TopListable } from '@zcode/bizos'
import { type MEDIA_TYPES, PAGE_NAMES } from '@zcode/mixpanel'
import type { Origin } from '@zcode/nr-utils'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import type { MessageField } from '@zcode/nr/es/shared/utils/message'
import { getPreviousLocation } from '@zcode/router-utils'
import type { match } from '@zcode/router-utils'
import type {
  Listable,
  MaybeEmptyProperty,
  Pin,
  RawPinInventorySearch,
  State,
} from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { routes } from '@zcode/zumper-routes'
import { getCurrentLocation } from '../../../inspectors/index'
import { logger } from '../../../logger'
import type { Dispatch, GetState } from '../../../modules/types'
import { selectEntity } from '../../detail/modules/entity/selectors/index'
import { getPageNameFromRouteProps } from '../../mixpanel/helpers/getPageNameFromRouteProps'
import { getFilters } from './getFilters'

export const getDetailEventProps = (
  entity: MaybeEmpty<Listable> | MaybeEmptyProperty | Pin<RawPinInventorySearch>,
  match: match | null,
  state: State
) => {
  return {
    // passing in false to the isBubble parameter because npr still relies on that method
    ...getDetailEventProperties(entity, state, false),
    pageType: getTraktorPageType(entity, match, state),
  }
}

export const getTraktorPageType = (
  entity: MaybeEmpty<Listable> | MaybeEmptyProperty | Pin<RawPinInventorySearch> | TopListable,
  match: match | null,
  state: State
) => {
  const { isActive } = entity
  const isAddressInactive = selectIsAddressInactive(state)
  let pageName = getPageNameFromRouteProps({ routeProps: { location: window.location } })
  if (pageName === PAGE_NAMES.RENT_RESEARCH_DETAIL || pageName === PAGE_NAMES.RENT_RESEARCH_HOME) {
    return pageName
  }
  const isNewDetail = !!match && isDetailPageNew(state)
  if (
    pageName === PAGE_NAMES.VERTICAL_GALLERY &&
    typeof window.history.state === 'object' &&
    window.history.state
  ) {
    const previousLocation = getPreviousLocation(window.history.state)
    if (
      previousLocation?.pathname.startsWith('/address/') &&
      selectAddress(state).url === previousLocation.pathname.substring(9) &&
      selectEntity(state)?.paUrl === previousLocation.pathname.substring(9)
    ) {
      // The URL of the vertical gallery page is a detail page URL with ?gallery
      // added onto the end. But if the vertical gallery is opened from an address
      // page, then, for analytics purposes, we want to consider it an Address page.
      pageName = PAGE_NAMES.PERM_ADDRESS
    }
  }

  const isAddressOnBuilding = selectIsAddressOnBuilding(state)
  const hasListingIdParam = new URLSearchParams(window.location.search).has('listing_id')
  const isSingleUnitAddress = selectIsSingleUnit(state)

  const addressPageName =
    `${isAddressInactive ? 'Inactive' : 'Active'} New ${isSingleUnitAddress ? 'Single' : 'Multi'} Address${
      selectIsOnlyActiveUnitOfMultiUnit(state)
        ? ' - Single Active Unit'
        : hasListingIdParam
          ? ' - Unit'
          : ''
    }` as const

  if (isAddressOnBuilding) {
    return addressPageName
  }

  return pageName === PAGE_NAMES.DETAIL || pageName === PAGE_NAMES.VERTICAL_GALLERY
    ? `${isActive ? 'Active' : 'Inactive'} ${isNewDetail ? 'New ' : ''}${entity.isBuilding ? 'Building' : 'Listing'}`
    : pageName === PAGE_NAMES.PERM_ADDRESS
      ? addressPageName
      : undefined
}

export interface AdditionalProps {
  action:
    | 'copy link'
    | 'open'
    | 'Email'
    | 'Facebook'
    | 'Pinterest'
    | 'Show Floorplan Expand'
    | 'Show Floorplan Summary'
    | 'Show Floorplans Close'
    | 'Twitter'
    | 'View Details Close'
    | 'View Details Expand'
  buildingId: number | null | undefined
  carouselAttributes?: {
    action: string
    index: number
    mediaId?: number
  }
  completedFields: MessageField[]
  createdAlert: boolean | undefined
  ctaName: string
  currentTab?: string
  customerName: string
  hasCustomizedMessage: boolean | undefined
  isFilterAd: boolean | null | undefined
  link: string
  mediaType: (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES]
  messageId: string
  messagedListingIds: readonly number[] | undefined
  messagedPropertyIds: ReadonlyArray<Listable['uId']>
  oneTap: boolean
  origin: Origin
  pageType?: string
  propertyId: ReturnType<typeof getTraktorEntityId>
  propertyIds: ReadonlyArray<Listable['uId']>
  railLocation: number
  review: unknown
  section: string
  shownPropertyIds: ReadonlyArray<Listable['uId']>
  shownSimilarPropertyIds: ReadonlyArray<ReturnType<typeof getTraktorEntityId>>
  similarProperty: ReturnType<typeof getEntityProperties>
  starsSelected: unknown
  user: ReturnType<typeof getUser>
  zoeUserId?: string
}

export interface TrackDetailEventProps {
  additionalProps?: Readonly<Partial<AdditionalProps>>
  entity?: MaybeEmpty<Listable> | MaybeEmptyProperty | Pin<RawPinInventorySearch> | null | undefined
  name: string
}

export const trackDetailEvent =
  ({ additionalProps, entity, name }: TrackDetailEventProps) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState()

    const user = getUser(state)
    const filters = getFilters(state, entity)
    const location = getCurrentLocation(state)

    const detailEntity = entity || selectEntity(state)

    if (!detailEntity) {
      logger.error(name, new Error('Attempting a traktor detail event with no entity'))
      return
    }

    const { match } = dispatch(getRouteProps({ routes }))
    const detailEventProps = getDetailEventProps(detailEntity, match, state)
    const props = { ...detailEventProps, ...additionalProps }

    dispatch(event({ name, props, user, filters, location }))
  }
